import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { ArrowLeft, Trash02 } from "untitledui-js-base";
import { getShortAddressFromAddress } from "shared/addressUtils";
import { useNavigate } from "react-router-dom";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { useMedia } from "shared/providers";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { Unit } from "types/landlord/address";
import { cx } from "shared/utils/classnames";
import { IconButton, IconButtonSize } from "shared/components";
import { Loader } from "shared/components/Loader";
import { RefreshCCW02 } from "untitledui-js-base";
import moment from "moment";

interface HeaderBreadcrumbSectionProps {
  unit?: Unit;
  onDeleteUnit: () => void;
  isSingleSyncSupported: boolean;
  isSyncing: boolean;
  handleRequestSync: () => void;
  lastSuccessSyncedAt?: string;
}

export const HeaderBreadcrumbSection = ({
  unit,
  onDeleteUnit,
  isSingleSyncSupported,
  isSyncing,
  handleRequestSync,
  lastSuccessSyncedAt
}: HeaderBreadcrumbSectionProps) => {
  const dictionary = useUnitsPropertiesDictionary();
  const { isMobile } = useMedia();
  const navigate = useNavigate();

  const getDeleteTooltip = () => {
    if (unit?.pms_id) return `You can't delete a PMS ${dictionary.unit}`;

    if (!unit?.can_delete)
      return `You can't delete a ${dictionary.unit} with applications`;

    return "";
  };

  return (
    <div className="flex items-center justify-between border-b border-alpha-40 py-2 md:py-0 pl-3 md:px-6 md:pb-3">
      {isMobile ? (
        <Button
          borderLess
          size={ButtonSize.SM}
          Icon={ArrowLeft}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      ) : (
        <BreadCrumbs
          crumbs={
            unit?.property
              ? [
                  {
                    title: dictionary.Properties,
                    href: "/properties"
                  },
                  {
                    title:
                      unit.property.name ||
                      getShortAddressFromAddress(unit.property.address) ||
                      `current ${dictionary.property}`,
                    href: `/properties/${unit.property.id}`
                  },
                  {
                    title: unit.title,
                    href: `/properties/${unit.property.id}/units/${unit.id}`
                  }
                ]
              : []
          }
        />
      )}

      <div className="flex items-center gap-2">
        {isSingleSyncSupported && (
          <div className="flex items-center gap-2">
            <p className="text-xs font-medium text-content-200 hidden md:block">
              Last sync:{" "}
              {lastSuccessSyncedAt
                ? moment(lastSuccessSyncedAt).format("MMM DD, YYYY, h:mm A")
                : "N/A"}
            </p>
            {isSyncing ? (
              <Loader size={32} />
            ) : (
              <IconButton
                className="shrink-0 bg-transparent hover:bg-alpha-40"
                borderLess
                Icon={RefreshCCW02}
                onClick={handleRequestSync}
                size={IconButtonSize.SM}
              />
            )}
          </div>
        )}
        <Button
          borderLess
          variant={ButtonVariant.BASE}
          size={ButtonSize.SM}
          className={cx(
            "stroke-content-400 text-content-400 hover:bg-alpha-40",
            !unit?.can_delete || Boolean(unit.pms_id) ? "!text-alpha-100" : ""
          )}
          onClick={onDeleteUnit}
          Icon={Trash02}
          disabled={!unit?.can_delete || Boolean(unit.pms_id)}
          tooltip={getDeleteTooltip()}
        >
          <p className="hidden md:flex">Delete {dictionary.unit}</p>
        </Button>
      </div>
    </div>
  );
};
